import { useCallback } from 'react'
import { ApolloError, useLazyQuery } from '@apollo/client'
import { graphql } from '~publish/graphql'
import type { Suggestion } from '~publish/gql/graphql'

export const REACH_LIMIT_ERROR =
  '[Comments] You have reached the maximum number of suggestions for your plan'

export type CommentSuggestionsQueryParams = {
  commentId: string
  regenerate?: boolean
}

type UseCommentSuggestionsReturn = {
  fetchCommentSuggestions: (params: CommentSuggestionsQueryParams) => Promise<{
    data: Suggestion[] | undefined
    error: string | null
  }>
}

export const getCommentSuggestionsQuery = graphql(`
  query commentSuggestions($input: CommentSuggestionsInput!) {
    commentSuggestions(input: $input) {
      suggestions {
        text
        source
      }
    }
  }
`)

/**
 * Hook to fetch AI-generated comment suggestions for given comments.
 *
 * @returns {UseCommentSuggestionsReturn} An object containing the fetchCommentSuggestions function
 *
 * @example
 * const { fetchCommentSuggestions } = useCommentSuggestions()
 * const { data, loading, error } = await fetchCommentSuggestions({
 *   commentId: '123456789012123456789012'
 *   regenerate: true
 * })
 */
export function useCommentSuggestions(): UseCommentSuggestionsReturn {
  const [fetchSuggestions] = useLazyQuery(getCommentSuggestionsQuery, {
    fetchPolicy: 'network-only',
  })

  const fetchCommentSuggestions = useCallback(
    async (params: CommentSuggestionsQueryParams) => {
      const { commentId } = params

      if (!commentId) {
        return {
          data: undefined,
          error: 'Comment Id is required',
        }
      }
      try {
        const { data, error } = await fetchSuggestions({
          variables: {
            input: {
              commentId,
              regenerate: params.regenerate,
            },
          },
        })

        if (error instanceof ApolloError) {
          if (error.message.includes(REACH_LIMIT_ERROR)) {
            // Send event to Clarity to easily track errors in the community
            if (window.clarity) {
              window.clarity('set', 'community_comment_suggestions_reach_limit')
            }
          } else {
            if (window.clarity) {
              window.clarity(
                'set',
                'community_comment_suggestions_error',
                commentId,
              )
            }
          }

          return {
            data: undefined,
            error: error.message.includes(REACH_LIMIT_ERROR)
              ? 'reachLimit'
              : null,
          }
        }

        return {
          data: data?.commentSuggestions?.suggestions,
          error: null,
        }
      } catch (error) {
        // Log the error details for debugging
        console.error('Error fetching comment suggestions:', error);
        // Send event to Clarity to easily track errors in the community
        if (window.clarity) {
          window.clarity(
            'set',
            'community_comment_suggestions_error',
            commentId,
          )
        }

        /* We are failing silently here because we don't want to break the app if the quick replies fail to load */
        return {
          data: undefined,
          error: 'Error fetching suggestions',
        }
      }
    },
    [fetchSuggestions],
  )

  return { fetchCommentSuggestions }
}
